import { useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/useAuth";
import { OverflowMenu } from "../components/common/OverflowMenu";
import { CreateProjectModal } from "../components/projects/CreateProjectModal";
import { DeleteProjectModal } from "../components/projects/DeleteProjectModal";
import { AppShell } from "../components/shell/Shell";
import { useProjects } from "../hooks/useProjects";
import type { Project } from "../types/projects";

export function ProjectsListPage() {
  const { user } = useAuth();
  const { data, isLoading, isError, error } = useProjects();
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);

  if (!user) {
    return <div role="status">Loading…</div>;
  }

  return (
    <AppShell width="workspace">
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "2rem",
        }}
      >
        <h1 style={{ margin: 0 }}>Projects</h1>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          data-testid="new-project-button"
          style={primaryBtn}
        >
          + New Project
        </button>
      </header>

      {isLoading && <div role="status">Loading projects…</div>}
      {isError && (
        <div role="alert" style={{ color: "#b91c1c" }}>
          Failed to load projects: {error instanceof Error ? error.message : "Unknown error"}
        </div>
      )}
      {data && data.projects.length === 0 && (
        <div data-testid="projects-empty-state" style={{ color: "#555" }}>
          No projects yet. Click <em>+ New Project</em> to create one.
        </div>
      )}
      {data && data.projects.length > 0 && (
        <ul
          data-testid="projects-list"
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
            gap: "1rem",
          }}
        >
          {data.projects.map((p) => (
            <ProjectCard
              key={p.id}
              project={p}
              onDelete={() => setDeleteTarget(p)}
            />
          ))}
        </ul>
      )}

      {showCreate && (
        <CreateProjectModal onClose={() => setShowCreate(false)} />
      )}
      {deleteTarget && (
        <DeleteProjectModal
          projectId={deleteTarget.id}
          projectName={deleteTarget.name}
          hasBoundary={deleteTarget.boundary_layer_id !== null}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => setDeleteTarget(null)}
        />
      )}
    </AppShell>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "#635bff",
  color: "#ffffff",
  border: "none",
  padding: "0.55rem 1rem",
  borderRadius: 6,
  cursor: "pointer",
  fontWeight: 500,
};

/**
 * Clickable project card with a hover highlight. The card body is
 * wrapped in a ``<Link>`` so the entire content area navigates to
 * the project — the overflow menu sits outside the link to keep its
 * action separate. Inline styles + a local hover state keep the
 * component self-contained (no global CSS file needed for one rule).
 */
function ProjectCard({
  project,
  onDelete,
}: {
  project: Project;
  onDelete: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <li
      data-testid={`project-card-${project.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...cardBaseStyle,
        ...(hovered ? cardHoverStyle : {}),
      }}
    >
      <Link
        to={`/projects/${project.id}`}
        data-testid={`project-link-${project.id}`}
        style={cardLinkStyle}
      >
        <div
          style={{
            fontSize: "1.125rem",
            fontWeight: 600,
            color: "#0a0e27",
          }}
        >
          {project.name}
        </div>
        <div
          style={{
            color: "#666",
            fontSize: "0.875rem",
            marginTop: "0.35rem",
          }}
        >
          {project.description || <em>No description</em>}
        </div>
        <div
          style={{
            color: "#8898aa",
            fontSize: "0.75rem",
            marginTop: "0.5rem",
          }}
        >
          status: {project.status}
        </div>
      </Link>
      <OverflowMenu
        triggerTestId={`project-overflow-${project.id}`}
        ariaLabel={`More actions for ${project.name}`}
        items={[
          {
            label: "Delete project",
            destructive: true,
            onSelect: onDelete,
            "data-testid": `project-overflow-delete-${project.id}`,
          },
        ]}
      />
    </li>
  );
}

const cardBaseStyle: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: "0.5rem",
  padding: "1.25rem",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "1rem",
  background: "#ffffff",
  cursor: "pointer",
  transition:
    "border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
};

const cardHoverStyle: React.CSSProperties = {
  borderColor: "#635bff",
  boxShadow:
    "0 0 0 1px rgba(99, 91, 255, 0.15), 0 6px 16px -8px rgba(50, 50, 93, 0.15)",
  transform: "translateY(-1px)",
};

const cardLinkStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  textDecoration: "none",
  color: "inherit",
  display: "block",
};
